import React, { useCallback, useState } from 'react';
import { queryCache } from 'react-query';
import { GradientTextAction, InputLabel, Poppins } from 'src/common';
import Button from 'src/components/form/Button';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import { ValidationErrorItem, IncidentCSV, ParseResult } from './types';
import { generateCsvTemplateLink, pathNames } from './util';
import { selectUserOption } from 'src/utils/misc';
import { useAuth } from 'src/state/auth';
import CSelect from 'src/components/form/Select';
import { IncidentType, incidentTypeOptions } from 'src/api/types';
import { IncidentCsvHeaders } from 'src/api/types/misc';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { Alert } from '@mui/material';
import { Dropzone, DzoneWrap, ErrorItem, ErrorLog } from 'src/common/dropzone';
import { IncidentInput, IncidentsQuery, useIncidentsQuery, useUpsertIncidentsMutation } from 'src/graphql/_generated';
import { convertDateToISO, convertToString, delimiters, validateRow } from 'src/utils/csv';
import { useCache } from 'src/graphql/useCache';
import { incidentValidationSchema } from 'src/components/formComposed/IncidentForm/util';

interface FromCsvProps {
  onClose: () => void;
  setIsScvDirty: (isDirty: boolean) => void;
  state: {
    file: File | null;
    setFile: (file: File | null) => void;
    readyForUpload: boolean;
    setReadyForUpload: (ready: boolean) => void;
  };
}

export const FromCsv: React.FC<FromCsvProps> = ({ onClose, setIsScvDirty, state }) => {
  const { usersOptios, workspaceId } = useAuth();
  const [error, setError] = useState('');
  const [isUploadSuccessful, setIsUploadSuccessful] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrorItem[]>([]);
  const [validIncidents, setValidIncidents] = useState<IncidentInput[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [overrideIncidents, setOverrideIncidents] = useState<IncidentsQuery['incidents']>([]);

  const { file, setFile, readyForUpload, setReadyForUpload } = state;

  const { data: { incidents: existingIncidents = [] } = {} } = useIncidentsQuery();

  const [upsertIncidents] = useUpsertIncidentsMutation();
  const { refetchQuery } = useCache();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    mpEvent(MPEvents.FileLoaded, {
      modal: 'Add Incident modal',
      tags: ['INCIDENT'],
    });
    if (acceptedFiles?.length > 0) {
      const selectedFile = acceptedFiles[0];
      setFile(selectedFile);
      setError('');
      setIsUploadSuccessful(false);
      setValidationErrors([]);
      parseCSVToJSON(selectedFile);
      setIsScvDirty(true);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'text/csv': ['.csv'],
    },
  });

  const parseCSVToJSON = (file: File) => {
    const tryParse = (delimiter: string) =>
      new Promise<ParseResult<IncidentCSV>>((resolve, reject) => {
        Papa.parse(file, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true,
          delimiter,
          complete: resolve,
          error: reject,
        });
      });

    (async () => {
      let result: ParseResult<IncidentCSV> | null = null;
      for (const delim of delimiters) {
        const parsed = await tryParse(delim);
        if (parsed.data && Object.keys(parsed.data[0] || {}).length > 2) {
          result = parsed;
          break;
        }
      }

      if (!result || !result.data.length) {
        setValidationErrors([
          { rowIndex: 0, errors: [{ path: 'Data', message: 'No incident data found in the file.' }] },
        ]);
        return;
      }

      if (result.data.length > 60) {
        setError('The number of incidents exceeds the limit of 60.');
        return;
      }

      const mappedIncidents: IncidentInput[] = result.data.map((el) => {
        return {
          ...(el[IncidentCsvHeaders.ID] && { id: convertToString(el[IncidentCsvHeaders.ID]) }),
          name: convertToString(el[IncidentCsvHeaders.Name] || ''),
          ...(el[IncidentCsvHeaders.RefID] && { ref: convertToString(el[IncidentCsvHeaders.RefID]) }),
          ...(el[IncidentCsvHeaders.Description] && {
            description: convertToString(el[IncidentCsvHeaders.Description]),
          }),
          ...(el[IncidentCsvHeaders.Source] && { source: convertToString(el[IncidentCsvHeaders.Source]) }),
          ...(el[IncidentCsvHeaders.Date] && {
            dateOfIncident: convertDateToISO(el[IncidentCsvHeaders.Date]),
          }),
          ...(el[IncidentCsvHeaders.Owner] && {
            owner: { text: convertToString(el[IncidentCsvHeaders.Owner]) },
          }),
          ...(el[IncidentCsvHeaders.Impact] && { impact: convertToString(el[IncidentCsvHeaders.Impact]) }),
          ...(el[IncidentCsvHeaders.FinancialImpactUpper] && {
            financialImpactUpper: el[IncidentCsvHeaders.FinancialImpactUpper],
          }),
          ...(el[IncidentCsvHeaders.FinancialImpactLower] && {
            financialImpactLower: el[IncidentCsvHeaders.FinancialImpactLower],
          }),
          ...(el[IncidentCsvHeaders.Type] && {
            incidentType: convertToString(el[IncidentCsvHeaders.Type]) as IncidentType,
          }),
          ...(el[IncidentCsvHeaders.Severity] && {
            severity: convertToString(el[IncidentCsvHeaders.Severity]),
          }),
        };
      });

      const promises = mappedIncidents.map((row, idx) =>
        validateRow({ row, rowIndex: idx + 1, schema: incidentValidationSchema, pathNames }),
      );
      const res = await Promise.all(promises);
      const errorLog = res.filter((el) => el?.errors?.length) as ValidationErrorItem[];

      if (errorLog.length) {
        setValidationErrors(errorLog);
        setIsUploadSuccessful(false);
        setReadyForUpload(false);
        setOverrideIncidents([]);
      } else {
        setValidIncidents(mappedIncidents);
        setValidationErrors([]);
        setError('');
        setReadyForUpload(true);
        setOverrideIncidents(existingIncidents.filter((el) => mappedIncidents.some((m) => m.id === el.id)));
      }
    })();
  };

  const save = async () => {
    mpEvent(MPEvents.ButtonClick, {
      button: 'Upload',
      modal: 'Add Incident modal',
      tags: ['INCIDENT'],
    });

    setReadyForUpload(false);
    setIsUploading(true);

    const data = validIncidents.map((el) => {
      const { userAdd } = selectUserOption({
        usersOptios,
        selectedUser: { text: el.owner?.text || '' },
      });
      return { ...el, owner: userAdd };
    });

    await upsertIncidents({ variables: { incidentsData: data, workspaceId } })
      .then(() => {
        queryCache.invalidateQueries();
        refetchQuery({ include: ['incidents'] });
        setIsScvDirty(false);
      })
      .catch((err: Error) => {
        setError(err?.message || 'Server error');
      });

    setIsUploading(false);
    setIsUploadSuccessful(true);
    setReadyForUpload(false);
  };

  const resetUpload = () => {
    setIsUploadSuccessful(false);
    setValidationErrors([]);
    setError('');
    setFile(null);
    setReadyForUpload(false);
  };

  return (
    <DzoneWrap className="h-padding">
      {!!overrideIncidents.length && <Alert severity="warning">This upload will modify existing incident data.</Alert>}
      <a href={generateCsvTemplateLink()} download="incident_template.csv" style={{ textDecoration: 'none' }}>
        <GradientTextAction
          $underline
          onClick={() =>
            mpEvent(MPEvents.DownloadCSVTemplate, {
              modal: 'Add Incident modal',
              tags: ['INCIDENT'],
            })
          }
          weight={500}
        >
          Download CSV Template
        </GradientTextAction>
      </a>
      {!isUploadSuccessful && (
        <Dropzone {...getRootProps()}>
          <input {...getInputProps()} />
          <Poppins px={14}>
            {isDragActive ? 'Drop the file here ...' : 'Drag and drop a file here, or click to select file'}
          </Poppins>
        </Dropzone>
      )}

      {file && <Poppins px={14}>File selected: {file.name}</Poppins>}
      {file && validationErrors.length === 0 && (
        <Poppins px={14}>Incidents ready for upload: {validIncidents.length}</Poppins>
      )}
      {isUploadSuccessful && <Poppins px={14}>File uploaded successfully!</Poppins>}
      {readyForUpload && !isUploadSuccessful && <Poppins px={14}>Validation successful, ready for upload</Poppins>}
      {validationErrors.length > 0 && (
        <ErrorLog>
          {validationErrors.map((error, index) => (
            <ErrorItem key={index}>
              <Poppins color="error" px={16} weight={600}>
                Error in row {error.rowIndex + 1}:
              </Poppins>
              {error.errors.map((e, idx) => (
                <Poppins px={14} color="error" css="display: block" key={idx}>{`${e.path}: ${e.message}`}</Poppins>
              ))}
            </ErrorItem>
          ))}
        </ErrorLog>
      )}
      {!isUploadSuccessful ? (
        <Button
          primary
          onClick={readyForUpload ? () => save() : resetUpload}
          css="width: 100%;"
          disabled={!file || !!validationErrors.length || isUploading}
        >
          {readyForUpload || isUploading ? 'Upload' : 'Select File'}
        </Button>
      ) : (
        <>
          <Button
            primary
            onClick={() => {
              resetUpload();
              setFile(null);
            }}
            css="width: 100%;"
          >
            Upload Another File
          </Button>

          <Button primary onClick={onClose} css="width: 100%;">
            Done
          </Button>
        </>
      )}

      {!file && (
        <>
          <Poppins px={14}>
            The valid values of Type are listed in the drop down below. Blank is also valid for each.
          </Poppins>

          <div className="grid-list">
            <div>
              <InputLabel>Type</InputLabel>
              <CSelect
                value={null}
                placeholder="Type"
                css="width: 100%;"
                menuPlacement="top"
                options={incidentTypeOptions}
              />
            </div>
          </div>
        </>
      )}
      {error && (
        <div className="error">
          <Poppins px={14}>{error}</Poppins>
        </div>
      )}
    </DzoneWrap>
  );
};
